import React, { useState } from 'react';
import { IconButton, Button, Typography } from '@telus-uds/components-web';
import EditIcon from '@telus-uds/palette-allium/build/web/icons/Edit';
import DeleteIcon from '@telus-uds/palette-allium/build/web/icons/Delete';
import Modal from '../../design/shared/modal';
import { Td, Th } from './css';
import ValidateLoader from '../../UI/validateLoader';
import { getFormattedDate } from '../../utils/utils';
import request from '../../utils/request';
import { formatCurrency } from '../../utils/currencyFormatter';
import { termName } from './constant';

export const table = ({
  tableData,
  isLoading,
  setModalVisible,
  setInitialValues,
  setOrderUpdate,
  setIsLoading
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [action, setAction] = useState('');
  const [updateValues, setUpdateValues] = useState(null);

  const handleDeleteClick = (e, id) => {
    if (e && e.preventDefault) e.preventDefault();
    setAction('delete the offer');
    setModalOpen(true);
    setUpdateValues(id);
  };

  const normalizeToArray = value => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return [value];
  };

  const handleEditClick = values => {

    const offerData = {
      ...values,
      channels: values.channels.map(data => data.id),
      dealerships: values.dealerships.map(data => data.id),
      expiredAt: new Date(values.expiredAt).toISOString().split('T')[0],
      speed: normalizeToArray(values.speed),
      offerCodesNotStackable: normalizeToArray(values.offerCodesNotStackable),
      // Normalize fields added after initial data entry — old records may have null
      offerType: values.offerType || '',
      tsbPlan: Array.isArray(values.tsbPlan) ? values.tsbPlan : [],
      bundled: Array.isArray(values.bundled) ? values.bundled.filter(v => v !== '') : [],
      term: Array.isArray(values.term) ? values.term.filter(v => v !== '') : [],
    };

    setUpdateValues(offerData);
    setModalOpen(true);
    setAction('edit the offer');
  };

  const cancelOffer = () => {
    setIsLoading(true);
    request.delete(`/offers/${updateValues}`).then(response => {
      setOrderUpdate(prev => !prev);
      setIsLoading(false);
    });
  };

  const updateOffer = () => {
    setInitialValues(updateValues);
    setModalVisible(true);
  };

  const onConfirm = () => {
    if (action === 'edit the offer') updateOffer();
    if (action === 'delete the offer') cancelOffer();
    setModalOpen(false);
  };

  if (isLoading) {
    return (
      <ValidateLoader
        message="Loading records, please wait…"
        isFormStep={isLoading}
      />
    );
  }

  const renderTableBody = () =>
    tableData.orders.map(field => {
      const details = field.offerDetails || [];
      const rowSpan = details.length || 1;

      return details.map((detail, detailIndex) => (
        <tr key={field.id}>
          {detailIndex === 0 && (
            <>
              <Td rowSpan={rowSpan}>
                {field.province.length > 3
                  ? `${field.province
                      .slice(0, 3)
                      .map(d => d)
                      .join(', ')}...`
                  : field.province.map(d => d).join(', ')}
              </Td>
              <Td rowSpan={rowSpan}>{field.product.join(', ')}</Td>
              <Td rowSpan={rowSpan}>
                {Array.isArray(field.term)
                  ? field.term.join(', ')
                  : field.term}
              </Td>
              <Td rowSpan={rowSpan}>{field.couponBasedDiscount}</Td>
              <Td rowSpan={rowSpan}>{field.offerCode}</Td>
              <Td rowSpan={rowSpan}>
                {Array.isArray(field.bundled)
                  ? field.bundled.join(', ')
                  : field.bundled}
              </Td>
              {/* <Td rowSpan={rowSpan}>{field.descriptionEn}</Td> */}
            </>
          )}

          <Td>{detail.offerDetailsEn}</Td>
          <Td style={{ whiteSpace: 'nowrap' }}>{formatCurrency(detail.value)}</Td>
          <Td style={{ whiteSpace: 'nowrap' }}>{termName[detail.frequency]}</Td>

          {detailIndex === 0 && (
            <>
              <Td rowSpan={rowSpan}>
                {getFormattedDate(field.expiredAt, 'YYYY-MM-DD')}
              </Td>
              <Td rowSpan={rowSpan} style={{ textAlign: 'center' }}>
                <IconButton
                  icon={EditIcon}
                  onPress={() => handleEditClick(field)}
                  accessibilityLabel="Edit"
                />
              </Td>
              <Td rowSpan={rowSpan} style={{ textAlign: 'center' }}>
                <IconButton
                  icon={DeleteIcon}
                  onPress={e => handleDeleteClick(e, field.id)}
                  accessibilityLabel="Delete"
                />
              </Td>
            </>
          )}
        </tr>
      ));
    });

  return (
    <>
      {tableData.orders.length > 0 ? (
        <div
          style={{
            width: '100%',
            overflow: 'auto',
            margin: '0 auto'
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed'
            }}
          >
            <thead>
              <tr>
                <Th style={{ width: '9%' }}>Province</Th>
                <Th style={{ width: '9%' }}>Product</Th>
                <Th style={{ width: '9%' }}>Term</Th>
                <Th style={{ width: '6%', whiteSpace: 'nowrap' }}>Coupon</Th>
                <Th style={{ width: '10%' }}>Offer Code</Th>
                <Th style={{ width: '6%', whiteSpace: 'nowrap' }}>Bundled</Th>
                {/* <Th>Offer Description (English)</Th> */}
                <Th style={{ width: '20%' }}>Offer Details (English)</Th>
                <Th style={{ width: '7%', whiteSpace: 'nowrap' }}>Value ($)</Th>
                <Th style={{ width: '8%', whiteSpace: 'nowrap' }}>Frequency</Th>
                <Th style={{ width: '8%' }}>Expiry Date</Th>
                <Th
                  colSpan={2}
                  style={{ textAlign: 'center', width: '8%', whiteSpace: 'nowrap' }}
                >
                  Actions
                </Th>
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#666',
            fontSize: '1rem'
          }}
        >
          No records found
        </div>
      )}
      <Modal
        open={modalOpen}
        setOpen={() => setModalOpen(false)}
        header="Confirmation Dialog"
      >
        <Typography block style={{ marginBottom: '1rem' }}>{`Are you sure you want to ${action} ?`}</Typography>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant={{ priority: 'low' }} onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant={{ priority: 'high' }} onClick={() => onConfirm()}>Confirm</Button>
        </div>
      </Modal>
    </>
  );
};

export default table;
